/**
 * Shared cookie-reader for the Cobuntu widgets.
 *
 * History: the auth cookie has gone through three name shapes over
 * the lifetime of the platform, and at any given time a logged-in
 * user might have any one of the three set in their browser:
 *
 *   1. `cobuntu_auth_<community-tag>`      (oldest — tag-suffixed)
 *   2. `cobuntu_auth`                       (canonical, current)
 *   3. `cobuntu_auth_<hostname-derived>`    (legacy compat — the apex's
 *                                            leftmost label, e.g.
 *                                            `belaescala` → `cobuntu_auth_belaescala`)
 *
 * The community-app's React side has always known about all three
 * (see lib/auth.ts SSR + lib/auth-client.ts client) and one-shot-
 * migrates whichever name it finds onto the canonical `cobuntu_auth`.
 * The widgets did NOT have this fallback — they read only the
 * canonical name — so a user whose only cookie was a legacy variant
 * would see the React side correctly mark them as signed-in while
 * every widget rendered the signed-out variant ("Sign in" button,
 * empty notifications, anon cart, etc.).
 *
 * That mismatch caused the "click Sign in → bounce back to /hub
 * without ever seeing the login form" bug reported by Diogo on PBN
 * (2026-06-11):
 *   - Header's Sign-in button was rendered by the auth widget, which
 *     saw NO cookie → showed the link.
 *   - `/login` page's React useAuth() saw the legacy cookie → flagged
 *     authed → `router.replace('/hub')` immediately.
 *   - Net result: user could never reach the login form, and the
 *     header kept showing "Sign in" anyway.
 *
 * That history is why the CLEAR path below still sweeps every legacy name.
 * The read and migrate helpers that used to live here are gone — see the
 * block further down for why the httpOnly flip retired both.
 *
 * The compat window for the legacy names is bounded by whatever the
 * React side picks — drop the corresponding branches here when the
 * React side drops them. As of 2026-06-11 the React reader still
 * supports the hostname-derived fallback (auth-client.ts line ~28);
 * dropping the tag-suffixed branch needs a separate decision because
 * older mobile clients may still write that shape.
 */

export const CANONICAL_AUTH_COOKIE = "cobuntu_auth";

/**
 * Derive the hostname-leftmost-label so the widget can match the
 * legacy `cobuntu_auth_<derived>` cookie shape. Mirrors
 * `lib/auth-client.ts::legacyDerivedName()`. Returns null when not
 * meaningful (no document, `localhost`, IP, etc.) so callers can
 * short-circuit.
 */
function hostnameDerivedTag(): string | null {
    if (typeof window === "undefined") return null;
    const host = window.location?.hostname || "";
    const tag = host.split(".")[0];
    if (!tag) return null;
    if (tag === "localhost") return null;
    // IPs (4 numeric parts) — also not a real label.
    if (/^\d+$/.test(tag)) return null;
    return tag;
}

/**
 * Read a single cookie by exact name. Returns the URL-decoded value or
 * null when absent. Wraps the typical `document.cookie.split('; ')`
 * pattern that every widget used to inline — single implementation now.
 */
export function readCookie(name: string): string | null {
    if (typeof document === "undefined") return null;
    const prefix = `${name}=`;
    const cookies = document.cookie.split("; ");
    for (const c of cookies) {
        if (c.startsWith(prefix)) {
            const raw = c.slice(prefix.length);
            try {
                return decodeURIComponent(raw);
            } catch {
                // Malformed cookie value — return as-is rather than null
                // so callers see something instead of silently treating
                // the user as anonymous.
                return raw;
            }
        }
    }
    return null;
}

/**
 * There is deliberately NO auth-cookie READ or MIGRATION helper here.
 *
 * `readAuthCookie` returned the session token and `migrateLegacyAuthCookie`
 * copied a legacy `cobuntu_auth_<tag>` cookie onto the canonical name. Both
 * are retired by the httpOnly flip (app platform S5), and the migration had
 * become actively dangerous:
 *
 *   - its "already migrated" guard was `if (readCookie(CANONICAL)) return;`.
 *     Under httpOnly that read returns null EVEN FOR A SIGNED-IN MEMBER, so
 *     the guard stopped firing and the migration started running for people
 *     it was never meant to touch.
 *   - what it then did was write `cobuntu_auth` with document.cookie. That
 *     cannot produce an httpOnly cookie; it produces a SECOND, JS-READABLE
 *     cookie of the same name sitting beside the real session — handing back
 *     the exact XSS-reachable token the flip removed, and leaving two cookies
 *     of one name for the server to disambiguate.
 *
 * It ran on every mount of six widgets. Removed rather than patched: the
 * 30-day legacy compat window closed long ago and the app side no longer
 * migrates either.
 *
 * Widgets learn who the visitor is from `shared/session.ts`, which asks the
 * server. Nothing in a widget needs the raw token.
 */

/**
 * Clear the canonical auth cookie at the host scope AND every parent-domain
 * scope down to (not including) the bare TLD. This removes a Domain-scoped
 * shared-login cookie the app may have set (e.g. Domain=belaescala.com) on
 * sign-out, WITHOUT the widget needing the shared-login allowlist. Clears at
 * scopes where no such cookie exists are harmless no-ops, and browsers ignore
 * public-suffix Domain values. Mirrors the app's clearClientAuthCookie.
 */
/**
 * End the session server-side (app platform S4).
 *
 * Once the cookie is httpOnly the browser cannot clear it, so the local clears
 * below stop working and THIS is what ends the session. keepalive because
 * every caller reloads or navigates immediately afterwards, which would
 * otherwise cancel the request and leave the visitor signed in.
 *
 * Same-origin: the widget runs on the customer apex, where the community-app
 * route handler lives.
 */
export function clearServerSession(): void {
  if (typeof fetch === "undefined") return;
  try {
    void fetch("/api/auth/session", {
      method: "DELETE",
      credentials: "same-origin",
      keepalive: true,
    });
  } catch {
    /* best-effort — local clears still run */
  }
}

export function clearCanonicalAuthCookieAllScopes(): void {
    if (typeof document === "undefined") return;
    document.cookie = `${CANONICAL_AUTH_COOKIE}=; path=/; max-age=0`;
    const host = (typeof location !== "undefined" ? location.hostname : "").split(":")[0];
    const labels = host.split(".");
    for (let i = 0; i <= labels.length - 2; i++) {
        const domain = labels.slice(i).join(".");
        document.cookie = `${CANONICAL_AUTH_COOKIE}=; path=/; max-age=0; domain=${domain}`;
    }
}
